import { createContext, forwardRef, useEffect, useRef, useState } from 'react';
import { useFloating, autoUpdate, offset, shift, useDismiss, useRole, useClick, useInteractions, FloatingFocusManager, FloatingList, useListNavigation, FloatingPortal, flip } from '@floating-ui/react';
import { MenuContainer } from './MenuContainer';
import { MenuItems } from './MenuItems';
import { Box } from '../../Box';
import { idBuilder } from '../../internal';
import { mergeSxStyle } from '../../common/mergeSxStyle';
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
export const MenuContext = /*#__PURE__*/createContext({
  getItemProps: () => ({}),
  activeIndex: null,
  setActiveIndex: () => {},
  isOpen: false
});
export const Menu = /*#__PURE__*/forwardRef((props, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const {
    icon,
    id,
    size,
    menuOptions,
    position = 'bottom-start',
    referenceProps,
    width,
    onSelect,
    ...rest
  } = props;
  const elementsRef = useRef([]);
  const labelsRef = useRef([]);
  const [activeIndex, setActiveIndex] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const anchorEl = referenceProps?.anchorEl;
  const open = referenceProps?.open;
  const onClose = referenceProps?.onClose;
  const handleOpenState = openState => {
    setIsOpen(openState);
    if (!openState && onClose) {
      onClose();
    }
  };
  const {
    refs,
    floatingStyles,
    context
  } = useFloating({
    placement: position || 'bottom-start',
    open: isOpen,
    onOpenChange: handleOpenState,
    middleware: [offset(4), shift(), flip()],
    whileElementsMounted: autoUpdate
  });
  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, {
    role: 'menu'
  });
  const listNav = useListNavigation(context, {
    listRef: elementsRef,
    activeIndex,
    selectedIndex,
    onNavigate: setActiveIndex
  });
  const {
    getReferenceProps,
    getItemProps,
    getFloatingProps
  } = useInteractions([click, dismiss, role, listNav]);
  useEffect(() => {
    if (anchorEl) {
      refs.setReference(anchorEl);
      setIsOpen(open);
    }
  }, [anchorEl, open]);
  const mergedFloatingStyles = mergeSxStyle(floatingStyles, {
    zIndex: 1300
  });
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsx(MenuContext.Provider, {
      value: {
        activeIndex,
        setActiveIndex,
        getItemProps,
        isOpen
      },
      children: /*#__PURE__*/_jsx(FloatingList, {
        elementsRef: elementsRef,
        labelsRef: labelsRef,
        children: isOpen && /*#__PURE__*/_jsx(FloatingPortal, {
          children: /*#__PURE__*/_jsx(FloatingFocusManager, {
            context: context,
            modal: false,
            initialFocus: 0,
            returnFocus: false,
            children: /*#__PURE__*/_jsx(Box, {
              className: "ids-menu-root",
              ref: refs.setFloating,
              sx: mergedFloatingStyles,
              ...getFloatingProps(),
              id: idBuilder('ids-menu-root', id),
              "data-testid": idBuilder('ids-menu-root', rest['data-testid']),
              children: /*#__PURE__*/_jsx(MenuContainer, {
                width: width,
                id: id,
                "data-testid": rest['data-testid'],
                children: /*#__PURE__*/_jsx(MenuItems, {
                  icon: icon,
                  menuOptions: menuOptions || [],
                  onClose: onClose,
                  onSelect: onSelect,
                  size: size,
                  id: id,
                  "data-testid": rest['data-testid']
                })
              })
            })
          })
        })
      })
    })
  });
});
Menu.displayName = 'Menu';